import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { usdCompact } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { RenovationBudgetRollup } from '@cc/types';

interface Props {
  scope: 'project' | 'room';
  /** Title shown in the card header (e.g. "Project budget" or the room name). */
  label: string;
  rollup: RenovationBudgetRollup;
  /** When set on project scope, used to compare against forecast. Optional. */
  budgetTotal?: number | null;
  className?: string;
}

/**
 * Three-layer budget rollup card: Estimated → Committed → Spent → Forecast,
 * with a variance badge. Used at both project and room scope.
 *
 * Variance colour rules:
 *   green   = variance ≤ 5% (or 0) — on track
 *   amber   = variance ≤ 15% — watch
 *   red     = variance > 15% — flag
 */
export function BudgetRollupCard({ scope, label, rollup, budgetTotal, className }: Props) {
  const tone = varianceTone(rollup);
  const pctOverBudget =
    budgetTotal && budgetTotal > 0 ? (rollup.forecast - budgetTotal) / budgetTotal : null;

  return (
    <Card className={className}>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="text-sm font-medium">
            {label}
            <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground">
              {scope}
            </span>
          </h3>
          <VarianceBadge rollup={rollup} />
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm sm:grid-cols-4">
          <Stat label="Estimated" value={usdCompact(rollup.estimated)} />
          <Stat label="Committed" value={usdCompact(rollup.committed)} />
          <Stat label="Spent" value={usdCompact(rollup.spent)} />
          <Stat
            label="Forecast"
            value={usdCompact(rollup.forecast)}
            tone={tone === 'over' ? 'danger' : tone === 'watch' ? 'warning' : 'default'}
          />
        </dl>

        {budgetTotal && pctOverBudget !== null && (
          <p className="text-[11px] text-muted-foreground">
            Budget {usdCompact(budgetTotal)}
            {pctOverBudget > 0
              ? ` · forecast ${(pctOverBudget * 100).toFixed(0)}% over`
              : pctOverBudget < 0
                ? ` · forecast ${(-pctOverBudget * 100).toFixed(0)}% under`
                : ' · forecast on budget'}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'default' | 'warning' | 'danger';
}) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          'font-medium tabular-nums',
          tone === 'danger' && 'text-rose-400',
          tone === 'warning' && 'text-amber-400',
        )}
      >
        {value}
      </dd>
    </div>
  );
}

/**
 * Small standalone variance pill. Exported so the per-room strip + item rows
 * can render it without dragging in the full card.
 */
export function VarianceBadge({ rollup }: { rollup: RenovationBudgetRollup }) {
  const tone = varianceTone(rollup);
  if (tone === 'good') {
    return <Badge variant="success">on track</Badge>;
  }
  const pct = rollup.variancePct;
  const pctLabel = pct === null ? '' : ` (${(pct * 100).toFixed(0)}%)`;
  const usd = usdCompact(Math.abs(rollup.variance));
  if (tone === 'watch') {
    return (
      <Badge variant="warning">
        +{usd}
        {pctLabel}
      </Badge>
    );
  }
  return (
    <Badge variant="danger">
      +{usd}
      {pctLabel} over
    </Badge>
  );
}

function varianceTone(rollup: RenovationBudgetRollup): 'good' | 'watch' | 'over' {
  if (rollup.estimated === 0) return 'good';
  const pct = rollup.variance / rollup.estimated;
  if (pct <= 0.05) return 'good';
  if (pct <= 0.15) return 'watch';
  return 'over';
}
